import { StateCreator } from 'zustand';
import { produce } from 'immer';
import { createAppStore } from '@marriott/mi-store-utils';

export interface SearchFormRenderedDataState {
  nightCount: number;
  monthSelected: number;
  yearSelected: number;
  tabSelected: string;
}

export interface SearchFormRenderedDataActions {
  setNightCount: (count: number) => void;
  setMonthSelected: (month: number) => void;
  setYearSelected: (year: number) => void;
  setTabSelected: (tabSelected: string) => void;
  initializeSearchFormRenderedData: (data: Partial<SearchFormRenderedDataState>) => void;
  isInitialState: () => boolean;
  resetSearchFormRenderedData: () => void;
}

export type SearchFormRenderedDataStore = SearchFormRenderedDataState & SearchFormRenderedDataActions;

export const initialSearchFormRenderedDataState: SearchFormRenderedDataState = {
  nightCount: 1,
  monthSelected: 0,
  yearSelected: 2022,
  tabSelected: '',
};

const searchFormRenderedDataStore: StateCreator<SearchFormRenderedDataStore> = (set, get) => ({
  ...initialSearchFormRenderedDataState,

  setNightCount: (count: number) => {
    set(
      produce((state: SearchFormRenderedDataStore) => {
        state.nightCount = count;
      })
    );
  },

  setMonthSelected: (month: number) => {
    set(
      produce((state: SearchFormRenderedDataStore) => {
        state.monthSelected = month;
      })
    );
  },

  setYearSelected: (year: number) => {
    set(
      produce((state: SearchFormRenderedDataStore) => {
        state.yearSelected = year;
      })
    );
  },

  setTabSelected: (tabSelected: string) => {
    set(
      produce((state: SearchFormRenderedDataStore) => {
        state.tabSelected = tabSelected;
      })
    );
  },

  initializeSearchFormRenderedData: (data: Partial<SearchFormRenderedDataState>) => {
    set(
      produce((state: SearchFormRenderedDataStore) => {
        if (data.nightCount !== undefined) state.nightCount = data.nightCount;
        if (data.monthSelected !== undefined) state.monthSelected = data.monthSelected;
        if (data.yearSelected !== undefined) state.yearSelected = data.yearSelected;
        if (data.tabSelected !== undefined) state.tabSelected = data.tabSelected;
      })
    );
  },

  isInitialState: () => {
    const currentState = get();
    return (
      currentState.nightCount === initialSearchFormRenderedDataState.nightCount &&
      currentState.monthSelected === initialSearchFormRenderedDataState.monthSelected &&
      currentState.yearSelected === initialSearchFormRenderedDataState.yearSelected &&
      currentState.tabSelected === initialSearchFormRenderedDataState.tabSelected
    );
  },

  resetSearchFormRenderedData: () => {
    set(
      produce((state: SearchFormRenderedDataStore) => {
        state.nightCount = initialSearchFormRenderedDataState.nightCount;
        state.monthSelected = initialSearchFormRenderedDataState.monthSelected;
        state.yearSelected = initialSearchFormRenderedDataState.yearSelected;
        state.tabSelected = initialSearchFormRenderedDataState.tabSelected;
      })
    );
  },
});

export const useSearchFormRenderedDataStore = createAppStore(searchFormRenderedDataStore, {
  usePersistentStore: false,
});

export const searchFormRenderedDataStoreSelectors = {
  nightCount: (state: SearchFormRenderedDataStore) => state.nightCount,
  monthSelected: (state: SearchFormRenderedDataStore) => state.monthSelected,
  yearSelected: (state: SearchFormRenderedDataStore) => state.yearSelected,
  tabSelected: (state: SearchFormRenderedDataStore) => state.tabSelected,
  setNightCount: (state: SearchFormRenderedDataStore) => state.setNightCount,
  setMonthSelected: (state: SearchFormRenderedDataStore) => state.setMonthSelected,
  setYearSelected: (state: SearchFormRenderedDataStore) => state.setYearSelected,
  setTabSelected: (state: SearchFormRenderedDataStore) => state.setTabSelected,
  initializeSearchFormRenderedData: (state: SearchFormRenderedDataStore) => state.initializeSearchFormRenderedData,
  resetSearchFormRenderedData: (state: SearchFormRenderedDataStore) => state.resetSearchFormRenderedData,
  isInitialState: (state: SearchFormRenderedDataStore) => state.isInitialState,
};
